import { useCallback, useState } from "react";
import { alertsService } from "@/services/alertsService";
import type {
  EvaluationResponse,
  ValidateResponse,
} from "@/components/Insights/insights";

export function useAlertValidation(trialId: string, insightId: string) {
  const [alertRule, setAlertRule] = useState("");
  const [isAlertRuleValid, setIsAlertRuleValid] = useState<boolean | null>(
    null,
  );
  const [validateResponse, setValidateResponse] =
    useState<ValidateResponse | null>(null);
  const [alertEvaluation, setAlertEvaluation] =
    useState<EvaluationResponse | null>(null);
  const [loadingValidation, setLoadingValidation] = useState(false);
  const [loadingEvaluation, setLoadingEvaluation] = useState(false);

  const handleRuleChange = useCallback((value: string) => {
    setAlertRule(value);
    setIsAlertRuleValid(null);
    setValidateResponse(null);
    setAlertEvaluation(null);
  }, []);

  const resetValidation = useCallback(() => {
    setIsAlertRuleValid(null);
    setValidateResponse(null);
    setAlertEvaluation(null);
    setLoadingValidation(false);
    setLoadingEvaluation(false);
  }, []);

  const validateAlertRule =
    useCallback(async (): Promise<ValidateResponse | null> => {
      setLoadingValidation(true);
      const payload = { alert_criteria: alertRule, insight_id: insightId };

      try {
        const validationResponse = await alertsService.validateAlertRule(
          trialId,
          payload,
        );
        setIsAlertRuleValid(validationResponse.condition_is_valid);
        setValidateResponse(validationResponse);
        setLoadingValidation(false);

        if (validationResponse.condition_is_valid) {
          setLoadingEvaluation(true);
          const evaluationResponse = await alertsService.evaluateAlert(
            trialId,
            payload,
          );
          setAlertEvaluation(evaluationResponse);
        } else {
          setAlertEvaluation(null);
        }

        return validationResponse;
      } catch (error) {
        console.error("Error validating alert:", error);
        setIsAlertRuleValid(false);
        setValidateResponse(null);
        setAlertEvaluation(null);
        return null;
      } finally {
        setLoadingValidation(false);
        setLoadingEvaluation(false);
      }
    }, [trialId, insightId, alertRule]);

  return {
    alertRule,
    setAlertRule,
    isAlertRuleValid,
    validateResponse,
    alertEvaluation,
    loadingValidation,
    loadingEvaluation,
    handleRuleChange,
    resetValidation,
    validateAlertRule,
  };
}
